import { reviewRequestLookupAuthoringReason } from './workflow-review-request-lookup-authoring-gate';
import {
  WorkflowAssignmentPolicy,
  WorkflowCapabilityReference,
  WorkflowDefinitionDocument,
  WorkflowDefinitionEdge,
  WorkflowDefinitionNode,
  WorkflowStepDeclaration,
  requireWorkflowPayloadVersion,
} from './platform-workflow-definition-lifecycle.models';
import {
  addWorkflowCapability,
  capabilityReferencesShapeError,
  capabilityIdentity,
  installedReference,
  sameCapabilityReference,
  WorkflowCapabilityCatalogEntry,
} from './workflow-capability-authoring';
import {
  hasEditableRouting,
  replacePresentation,
} from './workflow-presentation-authoring';
import {
  createDashboardPresentation,
  updateDashboardPresentation,
} from '@app/workflow-dashboards/workflow-dashboard-presentation';

import type {
  StepAuthoringCommand,
  StepAuthoringView,
  StepAuthoringCatalog,
} from './workflow-step-authoring.models';
import { preparationLookupAuthoringReason } from './workflow-preparation-lookup-authoring-gate';
export type {
  StepAuthoringFormField,
  StepAuthoringCommand,
  StepAuthoringView,
  StepAuthoringChildDefinition,
  StepAuthoringCatalog,
  StepCommand,
} from './workflow-step-authoring.models';
export const commandIdentity = (command: {
  capability: WorkflowCapabilityReference;
}) => capabilityIdentity(command.capability.key);

function workStep(document: WorkflowDefinitionDocument, nodeId: string) {
  const nodes = document.nodes.filter((item) => item?.id === nodeId);
  if (
    !hasEditableRouting(document) ||
    nodes.length !== 1 ||
    nodes[0].typeCode !== 'human_step'
  )
    throw new Error('Choose one unambiguous editable work step.');
  const commands = nodes[0].declaration?.behavior?.commands ?? [];
  if (new Set(commands.map(commandIdentity)).size !== commands.length)
    throw new Error(
      'This step has duplicate action identities. Inspect the saved step before editing it.',
    );
  return nodes[0];
}
function destinationNode(document: WorkflowDefinitionDocument, id: string) {
  const nodes = document.nodes.filter((node) => node?.id === id);
  if (
    nodes.length !== 1 ||
    !['human_step', 'terminal'].includes(nodes[0].typeCode)
  )
    throw new Error(
      'Choose one existing work step or completion point as destination.',
    );
  return nodes[0];
}
function reaches(
  edges: readonly WorkflowDefinitionEdge[],
  start: string,
  target: string,
): boolean {
  const visited = new Set<string>(),
    pending = [start];
  while (pending.length) {
    const id = pending.pop()!;
    if (id === target) return true;
    if (visited.has(id)) continue;
    visited.add(id);
    for (const edge of edges)
      if (edge.fromNodeId === id && edge.pathKindCode == null)
        pending.push(edge.toNodeId);
  }
  return false;
}

/** Authoring choices use the same forward-path reachability as persisted route validation. */
export function eligibleStepDestinations(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  movement: 'forward' | 'return',
): WorkflowDefinitionNode[] {
  const source = document.nodes.filter((node) => node.id === nodeId);
  if (source.length !== 1 || source[0].typeCode !== 'human_step') return [];
  return document.nodes.filter((candidate) => {
    if (
      candidate.id === nodeId ||
      !['human_step', 'terminal'].includes(candidate.typeCode) ||
      document.nodes.filter((node) => node.id === candidate.id).length !== 1
    )
      return false;
    const earlier = reaches(document.edges, candidate.id, nodeId);
    return movement === 'return'
      ? candidate.typeCode === 'human_step' && earlier
      : !earlier;
  });
}
function checkChangedPaths(
  document: WorkflowDefinitionDocument,
  edges: WorkflowDefinitionEdge[],
  changed: WorkflowDefinitionEdge[],
) {
  for (const edge of changed) {
    if (edge.pathKindCode === 'return') {
      if (
        destinationNode(document, edge.toNodeId).typeCode !== 'human_step' ||
        !reaches(edges, edge.toNodeId, edge.fromNodeId)
      )
        throw new Error(
          'A return visit must go to an earlier work step on its forward path.',
        );
    } else if (reaches(edges, edge.toNodeId, edge.fromNodeId)) {
      throw new Error(
        'This path would create a forward cycle. Use a declared return visit for earlier work.',
      );
    }
  }
}

export function initializeBlankStep(
  document: WorkflowDefinitionDocument,
  nodeId: string,
): WorkflowDefinitionDocument {
  const node = workStep(document, nodeId);
  if (node.declaration) return document;
  const policies =
    document.assignmentPolicies?.filter(
      (policy): policy is WorkflowAssignmentPolicy =>
        !!policy && policy.scopeCode === 'step' && policy.nodeId === nodeId,
    ) ?? [];
  if (
    policies.length > 1 ||
    policies.some(
      (policy) =>
        !policy.id?.trim() ||
        document.assignmentPolicies?.filter((item) => item?.id === policy.id)
          .length !== 1,
    )
  )
    throw new Error(
      'This step has ambiguous responsibility settings. Inspect responsibility before initializing it.',
    );
  return replacePresentation(
    {
      ...document,
      payloadSchemaVersion: requireWorkflowPayloadVersion(
        document.payloadSchemaVersion,
        '1.7',
      ),
      triggers: document.triggers ?? [],
      numbering: document.numbering ?? { mode: 'disabled' },
    },
    nodeId,
    {
      behavior: { assignmentPolicyId: policies[0]?.id ?? null, commands: [] },
      detail: {
        layout: 'workspace.two-column.v1',
        contextPlacement: 'side',
        blocks: [],
      },
      dashboards: [],
    },
  );
}

/** New human work starts usable; assignment roles remain an explicit author choice. */
export function initializeOperationalStep(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  catalog: StepAuthoringCatalog,
  capabilities: WorkflowCapabilityCatalogEntry[],
): WorkflowDefinitionDocument {
  const completion = catalog.commands.filter(
    (command) =>
      command.capability.key.provider === 'workflow' &&
      command.capability.key.code === 'workflow.step.complete' &&
      command.capability.key.version === 1 &&
      command.supportsCompletionRequirements === true &&
      command.completesStep !== false,
  );
  const worklists = catalog.views.filter(
    (view) =>
      view.capability.key.provider === 'platform' &&
      view.capability.key.kind === 'view' &&
      view.capability.key.code === 'workflow.preparation.work' &&
      view.capability.key.version === 1 &&
      view.scopes.includes('workflow-instance') &&
      view.contextParameters.some(
        (binding) => binding.source === 'selected-step-id',
      ),
  );
  if (
    catalog.version !== 1 ||
    completion.length !== 1 ||
    worklists.length !== 1
  )
    throw new Error(
      'The standard task and worklist are unavailable. Reload process support before adding this step.',
    );
  const interaction = catalog.interactions.filter(
    (item) =>
      item.code === 'generated-command-form' &&
      item.version === 1 &&
      sameCapabilityReference(item.command, completion[0].capability),
  );
  if (interaction.length !== 1)
    throw new Error(
      'The standard task page is unavailable. Reload process support before adding this step.',
    );
  let next = addStepCommand(document, nodeId, completion[0], capabilities);
  next = addStepView(next, nodeId, worklists[0], capabilities);
  const node = next.nodes.find((item) => item.id === nodeId)!;
  const declaration = node.declaration!;
  const dashboard = declaration.dashboards!.at(-1)!;
  return replacePresentation(next, nodeId, {
    ...declaration,
    dashboards: declaration.dashboards!.map((item) =>
      item.id === dashboard.id
        ? {
            ...updateDashboardPresentation(
              item,
              createDashboardPresentation(worklists[0], next, nodeId),
              worklists[0],
            ),
            label: node.displayName || 'Work',
          }
        : item,
    ),
  });
}
function pin(
  document: WorkflowDefinitionDocument,
  reference: WorkflowCapabilityReference,
  capabilities: WorkflowCapabilityCatalogEntry[],
): WorkflowDefinitionDocument {
  const found = capabilities.filter(
    (entry) =>
      capabilityIdentity(entry.key) === capabilityIdentity(reference.key),
  );
  if (
    found.length !== 1 ||
    !sameCapabilityReference(installedReference(found[0]), reference)
  )
    throw new Error(
      'The installed component contract has changed. Reload components before adding it.',
    );
  return addWorkflowCapability(document, found[0], capabilities);
}
export function addStepCommand(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  contract: StepAuthoringCommand,
  capabilities: WorkflowCapabilityCatalogEntry[],
): WorkflowDefinitionDocument {
  const unavailable = reviewRequestLookupAuthoringReason(contract.capability) || preparationLookupAuthoringReason(contract.capability);
  if (unavailable) throw new Error(unavailable);
  let next = initializeBlankStep(document, nodeId);
  if (
    contract.supportsCompletionRequirements === true ||
    (contract.completesStep === false &&
      (contract.supportsCaptureForm === true || !!contract.decisionEvidence))
  )
    next = {
      ...next,
      payloadSchemaVersion: requireWorkflowPayloadVersion(
        next.payloadSchemaVersion,
        '1.8',
      ),
    };
  next = pin(next, contract.capability, capabilities);
  for (const required of contract.condition?.requiredCapabilities ?? [])
    next = pin(next, required, capabilities);
  const declaration = next.nodes.find(
    (node) => node.id === nodeId,
  )!.declaration!;
  if (
    declaration.behavior.commands.some(
      (command) => commandIdentity(command) === commandIdentity(contract),
    )
  )
    throw new Error('This action is already attached to the step.');
  return replacePresentation(next, nodeId, {
    ...declaration,
    behavior: {
      ...declaration.behavior,
      commands: [
        ...declaration.behavior.commands,
        {
          capability: contract.capability,
          ...(contract.supportsCaptureForm === true
            ? {
                captureForm: {
                  version: ['1.19', '1.20', '1.21', '1.22'].includes(next.payloadSchemaVersion) ? 2 as const : 1 as const,
                  id: `form-${crypto.randomUUID()}`,
                  title: contract.label,
                  fields: [],
                },
              }
            : {}),
          requirements: contract.requirements
            .filter((rule) => rule.mandatory)
            .map((rule) => rule.code),
          outcomes: contract.outcomes
            .filter((outcome) => outcome.mandatory)
            .map((outcome) => ({
              code: outcome.code,
              label: outcome.label,
              reasonPolicy: outcome.defaultReasonPolicy,
              destinationNodeId: '',
            })),
        },
      ],
    },
  });
}
export function addStepView(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  view: StepAuthoringView,
  capabilities: WorkflowCapabilityCatalogEntry[],
): WorkflowDefinitionDocument {
  const next = pin(
    initializeBlankStep(document, nodeId),
    view.capability,
    capabilities,
  );
  const declaration = next.nodes.find(
    (node) => node.id === nodeId,
  )!.declaration!;
  const defaults = structuredClone(view.defaults);
  for (const parameter of view.contextParameters) {
    if (parameter.source !== 'selected-step-id')
      throw new Error(
        'This view needs a context binding not supported by this editor.',
      );
    defaults[parameter.parameter] = nodeId;
  }
  const dashboards = declaration.dashboards ?? [];
  const base = `dashboard-${dashboards.length + 1}`;
  let id = base,
    suffix = 2;
  while (dashboards.some((item) => item.id === id)) id = `${base}-${suffix++}`;
  return replacePresentation(next, nodeId, {
    ...declaration,
    dashboards: [
      ...dashboards,
      {
        id,
        label: view.label,
        view: view.capability,
        scope: view.scopes[0],
        columns: view.defaultColumns.filter(
          (field) =>
            field !== 'queueCode' ||
            !['1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(next.payloadSchemaVersion ?? ''),
        ),
        filterFields: [...view.filterFields],
        defaults,
        summaries: [...view.summaries],
        quickActions: [],
        queueScope: null,
      },
    ],
  });
}

/** Explicit Dashboard creation: exact installed source, fresh defaults and selected-step ownership. */
export function addStepDashboard(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  reference: WorkflowCapabilityReference,
  catalog: StepAuthoringCatalog,
  capabilities: WorkflowCapabilityCatalogEntry[],
): WorkflowDefinitionDocument {
  if (capabilityReferencesShapeError([reference]) || !Array.isArray(catalog?.views))
    throw new Error('Choose one exact installed dashboard source.');
  const matches = catalog.views.filter(view => view?.capability?.key && capabilityIdentity(view.capability.key) === capabilityIdentity(reference.key));
  if (matches.length !== 1 || !sameCapabilityReference(matches[0].capability, reference))
    throw new Error('The dashboard source contract is unavailable or ambiguous. Refresh sources and choose its exact installed reference.');
  const view = matches[0], key = reference.key;
  if (key.kind !== 'view' || key.version !== 1 || !(
    key.provider === 'platform' && ['workflow.preparation.work', 'workflow.review.records', 'workflow.review.work'].includes(key.code)
    || key.provider === 'hearings' && key.code === 'hearings.day-work'))
    throw new Error('This installed source does not support dashboard composition.');
  const node = workStep(document, nodeId), saved = node.declaration?.dashboards;
  if (saved != null && (!Array.isArray(saved) || saved.some(dashboard => !dashboard || typeof dashboard.id !== 'string' || !dashboard.id.trim())
    || new Set(saved.map(dashboard => dashboard.id)).size !== saved.length))
    throw new Error('Saved dashboard identities are unavailable or ambiguous. Their original settings are retained.');
  if (!view.defaults || typeof view.defaults !== 'object' || Array.isArray(view.defaults)
    || !Array.isArray(view.contextParameters) || !Array.isArray(view.scopes) || !view.scopes.length
    || !Array.isArray(view.columns) || ![view.defaultColumns, view.filterFields, view.summaries].every(values => Array.isArray(values) && values.every(value => typeof value === 'string')))
    throw new Error('The installed dashboard source defaults are unavailable. Refresh sources before adding it.');
  const label = node.displayName;
  if (typeof label !== 'string' || !label.trim() || label.trim().length > 120)
    throw new Error('Enter a step display name of up to 120 characters before adding its dashboard.');
  const next = addStepView(document, nodeId, view, capabilities);
  const target = next.nodes.filter(item => item.id === nodeId)[0], oldIds = new Set((saved ?? []).map(item => item.id));
  const created = target.declaration!.dashboards!.filter(dashboard => !oldIds.has(dashboard.id));
  if (created.length !== 1) throw new Error('The new dashboard identity could not be established. Saved dashboards are retained.');
  const dashboard = {
    ...updateDashboardPresentation(created[0], createDashboardPresentation(view, next, nodeId), view),
    label: label.trim(),
  };
  return replacePresentation(next, nodeId, { ...target.declaration!,
    dashboards: target.declaration!.dashboards!.map(item => item.id === dashboard.id ? dashboard : item) });
}

/** Persist graph-owned routes for completing actions and saved decisions. */
export {
  readStepOutcomeRoutes,
  nearestPreviousWorkSteps,
} from './workflow-step-outcome-routes';
export { applyStepOutcomeRoutes as applyComponentRoutes } from './workflow-step-outcome-routes';
export function setStepNextPath(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  destination: string,
): WorkflowDefinitionDocument {
  workStep(document, nodeId);
  destinationNode(document, destination);
  if (destination === nodeId)
    throw new Error('Choose another work step or completion point.');
  const edges = document.edges.filter((edge) => edge.fromNodeId === nodeId);
  if (
    edges.length > 1 ||
    edges.some(
      (edge) =>
        edge.outcomeCode != null ||
        edge.pathKindCode != null ||
        document.nodes.some(
          (node) =>
            node.id === edge.toNodeId && node.typeCode === 'routing_decision',
        ),
    )
  )
    throw new Error(
      'This step has conditional paths. Use Paths to inspect them.',
    );
  const id = edges[0]?.id ?? `${nodeId}-next`;
  const retained = document.edges.filter((edge) => edge.fromNodeId !== nodeId);
  if (retained.some((edge) => edge.id === id))
    throw new Error('The path identity is already used. Inspect Paths first.');
  const replacement = {
    ...edges[0],
    id,
    fromNodeId: nodeId,
    toNodeId: destination,
  };
  const next = [...retained, replacement];
  checkChangedPaths(document, next, [replacement]);
  return { ...document, edges: next };
}
